import { getEnumValues } from '../components/helpers/helpers';
import { EventDefinition } from '../monitoring/alerts/event-list/models';

export enum ThresholdWorseningDirection {
    none,
    up,
    down
}

export enum ThresholdLevel {
    info,
    watch,
    warning,
    critical,
    emergency
}
export enum ThresholdLevelActionType {
    none,
    possible,
    withinMinutes,
    immediate
}

export enum AirportThresholdGroupUsage {
    default = 0,
    origin = 1,
    destination = 2,
    alternate = 3
}

export enum ThresholdControlType {
    input,
    dropdown,
    checkbox
}

export interface UnitInfo {
    displayName: string;
    multiplier: number;
}

interface _ThresholdInfo {
    name: string;
    worseningDirection: ThresholdWorseningDirection;
    decimal: boolean;
    canRepeat: boolean;
    min: number;
    max: number;
    alertsOnly: boolean;
    disabled: boolean;
}

type DropdownThresholdInfo = _ThresholdInfo & {
    controlType: ThresholdControlType.dropdown;
    dropdownOptions: string[];
};

type CheckboxThresholdInfo = _ThresholdInfo & {
    controlType: ThresholdControlType.checkbox;
};

type InputThresholdInfo = _ThresholdInfo & {
    controlType: ThresholdControlType.input;
    // TODO: use :? instead of empty array everywhere and make it mutually exclusive with units property
    customUnits: UnitInfo[];
    units?: string;
};

export interface MonitorSettings {
    // threshold lists
    user: MonitorUserSet;
    company: MonitorCompanySet;
    eventDefinitions: Partial<Record<number, EventDefinition>>;
    thresholds: {
        aircraft: {
            displayOrder: AircraftThresholdType[];
            definitions: Record<AircraftThresholdType, ThresholdInfo>;
        };
        airport: {
            displayOrder: AirportThresholdType[];
            definitions: Record<AirportThresholdType, ThresholdInfo>;
        };
    };
}

export interface MonitorAirportThresholdSettingsItem {
    type: AirportThresholdType;
    priority: number;
    alerts: MonitorAirportThresholdAlerts;
}

export interface MonitorAirportOptions {
    tafWindowHours: number | null;
    airportMonitorStatusInclude: {
        originThresholds: boolean | null;
        destinationThresholds: boolean | null;
        alternateThresholds: boolean | null;
    };
}

export interface MonitorAirportSettingsSet {
    groups: MonitorAirportThresholdsGroupModel[];
    thresholdsSettings: MonitorAirportThresholdSettingsItem[];
    options: MonitorAirportOptions;
}

export interface MonitorAircraftSettingsBeaconCode {
    code: number;
    text: string;
}

export interface MonitorAircraftOptions {
    monitorOriginDepartureMinMinutes: number | null;
    monitorOriginWithoutDepartureTime: boolean | null;
    monitorDestinationDepartureMinMinutes: number | null;
    monitorDestinationWithoutArrivalTime: boolean | null;
    monitorAltDepartureMinMinutes: number | null;
    monitorAltWithoutArrivalTime: boolean | null;
    emergencyBeacons: MonitorAircraftSettingsBeaconCode[];
    ignoredCompanyBeaconCodes: number[];
    altitudeChangeWindowMinutes: number | null;
    suppressAltitudeMonitorMiles: number | null;
    monitorImpactDistance: {
        volcanicAsh: number | null;
        sigmet: number | null;
        convSigment: number | null;
        dsua: number | null;
        edrTurbulence: number | null;
        turbulence: number | null;
        icing: number | null;
        contrail: number | null;
        tcf: number | null;
    };
}

export interface MonitorAircraftSettingsSet {
    thresholds: MonitorAircraftThreshold[];
    options: MonitorAircraftOptions;
}

export interface MonitorSet {
    originalDbVersion?: number;
    originalFeVersion?: string;
    airport: MonitorAirportSettingsSet;
    aircraft: MonitorAircraftSettingsSet;
}

export type MonitorUserSet = MonitorSet;

export type MonitorCompanySet = MonitorSet & {
    mode: CompanyThresholdsMode;
};

export interface MonitorAircraftThreshold {
    type: AircraftThresholdType;
    priority: number; // TODO: shouldn't this be nullable to allow inheritance?
    enableState: ThresholdEnableState;
    info: MonitorThresholdLevelModel | null;
    watch: MonitorThresholdLevelModel | null;
    warning: MonitorThresholdLevelModel | null;
    critical: MonitorThresholdLevelModel | null;
    emergency: MonitorThresholdLevelModel | null;
    alerts: MonitorAircraftThresholdAlerts;
    ignoredInheritedLevels: ThresholdLevel[];
}

export interface MonitorThresholdAlert {
    value: boolean | null;
    minLevel: MonitorThresholdAlertMinimumLevel | null;
}

export interface MonitorThresholdAlertsBase {
    eventList: MonitorThresholdAlert;
    eventListAck: MonitorThresholdAlert;
    eventListImportant: MonitorThresholdAlert;
    email: MonitorThresholdAlert;
    logFile: MonitorThresholdAlert;
    popup: MonitorThresholdAlert;
    osAlert: MonitorThresholdAlert;
    overrideColor: MonitorThresholdAlert;
    voice: MonitorThresholdAlert;
    playSound: MonitorThresholdAlert;
    overrideColorHex: string | null;
}

export type MonitorAircraftThresholdAlerts = MonitorThresholdAlertsBase & {
    aircraftTag: MonitorThresholdAlert;
    zoomToAircraft: MonitorThresholdAlert;
    enableMarker: MonitorThresholdAlert;

    alertOnScheduled: MonitorThresholdAlert;
    alertOnPlanned: MonitorThresholdAlert;
    alertOnActive: MonitorThresholdAlert;
};

export type MonitorAirportThresholdAlerts = MonitorThresholdAlertsBase & {};

export interface MonitorAirportThresholdsGroupModel {
    airportId: string;
    usage: AirportThresholdGroupUsage;
    autoGenerated: boolean;
    isOff: boolean;
    thresholds: MonitorAirportThreshold[];
}

export interface MonitorAirportThreshold {
    type: AirportThresholdType;
    enableState: ThresholdEnableState;
    info: MonitorThresholdLevelModel | null;
    watch: MonitorThresholdLevelModel | null;
    warning: MonitorThresholdLevelModel | null;
    critical: MonitorThresholdLevelModel | null;
    emergency: MonitorThresholdLevelModel | null;
    ignoredInheritedLevels: ThresholdLevel[];
}

export interface MonitorAirportThresholdOptionsModel {
    type: AirportThresholdType;
    priority: number;
    alerts: MonitorAirportThresholdAlerts;
}

export interface MonitorThresholdLevelModel {
    repeat: boolean | null;
    triggerOnImproving: boolean | null;
    value: number;
    unitIndex: number | null;
    actionType: ThresholdLevelActionType;
    actionTimeMinutes: number | null;
}

export interface ThresholdsCustomEventEntry {
    code: number;
    displayName: string;
    spokenText: string;
    eventListText: string;
}

export type ThresholdInfo = DropdownThresholdInfo | CheckboxThresholdInfo | InputThresholdInfo;

export enum ThresholdEnableState {
    notSet,
    enabled,
    disabled
}

export enum MonitorThresholdAlertMinimumLevel {
    info,
    watch,
    warning,
    critical,
    emergency
}

// DO NOT CHANGE THIS
// TODO: find all places where this constant is not used and use it!
export const DECIMAL_THRESHOLD_DIVISOR = 100;

export enum AirportThresholdType {
    metarFlightCat = 1,
    metarVisibility = 2,
    metarCeiling = 3,
    metarWindSpeed = 4,
    metarWindGusts = 5,
    metarLastUpdate = 6,
    tafFlightCat = 7,
    tafVisibility = 8,
    tafCeiling = 9,
    tafWindSpeed = 10,
    tafLastUpdate = 11,
    departDelay = 12,
    arrivalDelay = 13,
    groundStop = 14,
    groundDelay = 15,
    faaClosure = 16,
    rvr = 17,
    diversionsTo = 18,
    diversionsFrom = 19,
    departureRate = 20,
    arrivalRate = 21,
    deIcingActive = 22,
    holdingInto = 23,
    monitorStatusChanged = 24
}

export enum CompanyThresholdsMode {
    notUsed,
    template,
    minimum,
    specified
}

export enum AircraftThresholdType {
    aptMonStatOrg = 1,
    aptMonStatDst = 2,
    aptMonStatAlt = 3,
    minPreDptNoFPFiled = 4,
    minPastPlanDepTime = 5,
    minPastEstArrTime = 6,
    emergencyBeaconCodeRec = 7,
    taxiTime = 8,
    milesOffRoute = 9,
    edctIssued = 10,
    milesFromDest = 11,
    timeSinceLastPosUpd = 12,
    diverting = 13,
    holdingDetected = 14,
    inDrawingObject = 15,
    altitudeChange = 16,
    volcAsh = 17,
    sigmet = 18,
    cnvSigmet = 19,
    turbulence = 20,
    edrTurbulence = 21,
    icing = 22,
    fobDiffActEst = 23,
    altDiffActEst = 24,
    goAround = 25,
    iataTurbulence = 26,
    pirepIce = 27,
    pirepTurb = 28,
    gtgnTurbulence = 29,
    dsua = 30,
    departureTimeChange = 31,
    etaChange = 32,
    scheduleReceived = 33,
    flightPlanReceived = 34,
    activeStatus = 35,
    departedStatus = 36,
    arrivedStatus = 37,
    outReceived = 38,
    offReceived = 39,
    onReceived = 40,
    inReceived = 41,
    monitorStatusChanged = 42,
    positionUpdated = 43,
    fobChanged = 44,
    routeIntersectArea = 45,
    routeNotIntersectArea = 46,
    fpmLateAtPosition = 47,
    fpmFobAlertLVL1 = 48,
    fpmFobAlertLVL2 = 49,
    fpmFobAlertLVL3 = 50,
    fpmLowAtPosition = 51,
    fpmMinOffRouteAtPosition = 52,
    fpmNMOffRouteAtPosition = 53,
    fpmPosRepNotReceived = 54,
    fpmMinPriorToEtopsEntry = 55,
    fpmMinPriorToRdspchPoint = 56,
    fpmMessageReceived = 57,
    fpmTurbulenceAlert = 58,
    fpmNewNotamForAirport = 59,
    fpmNewNotamForFir = 60,
    impactTcf = 61,
    impactContrails = 62,
    enterDrawingObject = 63,
    exitDrawingObject = 64,
    flightPlanChanged = 65,
    routeEteChanged = 66
}

export const CommonAircraftThresholdTypes = getEnumValues(AircraftThresholdType);
export const CommonAirportThresholdTypes = getEnumValues(AirportThresholdType);

export const commonAircraftThresholdDefinitions: Record<AircraftThresholdType, ThresholdInfo> = {
    [AircraftThresholdType.aptMonStatOrg]: {
        name: 'Origin airport monitor status',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        dropdownOptions: ['Info', 'Watch', 'Warning', 'Critical', 'Emergency'],
        min: 0,
        max: 4,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.aptMonStatDst]: {
        name: 'Dest airport monitor status',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        dropdownOptions: ['Info', 'Watch', 'Warning', 'Critical', 'Emergency'],
        min: 0,
        max: 4,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.aptMonStatAlt]: {
        name: 'Dest alt airport monitor status',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        dropdownOptions: ['Info', 'Watch', 'Warning', 'Critical', 'Emergency'],
        min: 0,
        max: 4,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.minPreDptNoFPFiled]: {
        name: 'Time prior to dep and no FP filed',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.minPastPlanDepTime]: {
        name: 'Time past planned dep',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.minPastEstArrTime]: {
        name: 'Time past est arrival',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.taxiTime]: {
        name: 'Taxi time',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.milesOffRoute]: {
        name: 'Off route',
        customUnits: [
            { displayName: 'NM', multiplier: 1 },
            { displayName: 'KM', multiplier: 1.852 }
        ],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 1000,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.milesFromDest]: {
        name: 'Distance from destination',
        customUnits: [
            { displayName: 'NM', multiplier: 1 },
            { displayName: 'KM', multiplier: 1.852 }
        ],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 1000,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.timeSinceLastPosUpd]: {
        name: 'Time since last rep pos update',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 120,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.diverting]: {
        name: 'Diverting',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.holdingDetected]: {
        name: 'Holding Detected',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.inDrawingObject]: {
        name: 'In drawing object',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.emergencyBeaconCodeRec]: {
        name: 'Emergency beacon code received*',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.edctIssued]: {
        name: 'EDCT issued*',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.altitudeChange]: {
        name: 'Altitude change',
        units: 'feet/min',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 1000,
        max: 100000,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.volcAsh]: {
        name: 'Impact: Volcanic ash',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.sigmet]: {
        name: 'Impact: SIGMET',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.cnvSigmet]: {
        name: 'Impact: Convective SIGMET',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.turbulence]: {
        name: 'Impact: Turbulence',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: ['Light', 'Moderate', 'Severe', 'Extreme'],
        min: 0,
        max: 3,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.edrTurbulence]: {
        name: 'Impact: EDR Turbulence',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: [
            '0.4 to < 0.5',
            '0.5 to < 0.6',
            '0.6 to < 0.7',
            '0.7 to < 0.8',
            '0.8 to < 0.9',
            '0.9 to < 1.0',
            'AC-Mod',
            'AC-Sev',
            'AC-Ext'
        ],
        min: 0,
        max: 8,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.icing]: {
        name: 'Impact: Icing',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: ['Trace', 'Light', 'Moderate', 'Heavy'],
        min: 0,
        max: 3,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.fobDiffActEst]: {
        name: 'Fuel: Actual below planned',
        units: '%',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 200,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.altDiffActEst]: {
        name: 'Altitude: Actual vs planned',
        customUnits: [
            { displayName: 'ft', multiplier: 1 },
            { displayName: 'm', multiplier: 0.3048 }
        ],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 50000,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.goAround]: {
        name: 'Go around detected',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.iataTurbulence]: {
        name: 'Impact: IATA Turbulence',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: [
            '0.4 to < 0.5',
            '0.5 to < 0.6',
            '0.6 to < 0.7',
            '0.7 to < 0.8',
            '0.8 to < 0.9',
            '0.9 to < 1.0',
            'AC-Mod',
            'AC-Sev',
            'AC-Ext'
        ],
        min: 0,
        max: 8,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.pirepIce]: {
        name: 'Impact: PIREP Icing',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: ['Moderate', 'Mod-Sev', 'Severe'],
        min: 0,
        max: 2,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.pirepTurb]: {
        name: 'Impact: PIREP Turbulence',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: ['Moderate', 'Mod-Sev', 'Severe', 'Sev-Extr', 'Extreme'],
        min: 0,
        max: 4,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.gtgnTurbulence]: {
        name: 'Impact: GTG Nowcast EDR Turb',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: [
            '0.4 to < 0.5',
            '0.5 to < 0.6',
            '0.6 to < 0.7',
            '0.7 to < 0.8',
            '0.8 to < 0.9',
            '0.9 to < 1.0',
            'AC-Mod',
            'AC-Sev',
            'AC-Ext'
        ],
        min: 0,
        max: 8,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.dsua]: {
        name: 'Impact: DSUA',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.departureTimeChange]: {
        name: 'Dep time change',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.etaChange]: {
        name: 'ETA change',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.scheduleReceived]: {
        name: 'Schedule received',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.flightPlanReceived]: {
        name: 'Flight plan received',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.activeStatus]: {
        name: 'Active',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.departedStatus]: {
        name: 'Departed',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.arrivedStatus]: {
        name: 'Arrived ',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.outReceived]: {
        name: 'OUT',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.offReceived]: {
        name: 'OFF',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.onReceived]: {
        name: 'ON',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.inReceived]: {
        name: 'IN',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.monitorStatusChanged]: {
        name: 'Monitor status change',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.positionUpdated]: {
        name: 'Aircraft position updated',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fobChanged]: {
        name: 'Fuel on board changed',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.routeIntersectArea]: {
        name: 'Aircraft route intersects area',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.routeNotIntersectArea]: {
        name: "Aircraft route doesn't intersect area",
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmLateAtPosition]: {
        name: 'FPM-Late at position',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmFobAlertLVL1]: {
        name: 'FPM-FOB Alert-LVL1',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmFobAlertLVL2]: {
        name: 'FPM-FOB Alert-LVL2',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmFobAlertLVL3]: {
        name: 'FPM-FOB Alert-LVL3',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmLowAtPosition]: {
        name: 'FPM-Low at position',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmMinOffRouteAtPosition]: {
        name: 'FPM-Min off route at position',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmNMOffRouteAtPosition]: {
        name: 'FPM-NM off route at position',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmPosRepNotReceived]: {
        name: 'FPM-Pos rep not received',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmMinPriorToEtopsEntry]: {
        name: 'FPM-Min prior to ETOPs entry',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmMinPriorToRdspchPoint]: {
        name: 'FPM-Min prior to rdspch point',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmMessageReceived]: {
        name: 'FPM-Message received',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmTurbulenceAlert]: {
        name: 'FPM-Turbulence alert',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmNewNotamForAirport]: {
        name: 'FPM-New NOTAM for airport',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.fpmNewNotamForFir]: {
        name: 'FPM-New NOTAM for FIR',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.impactTcf]: {
        name: 'Impact: TCF',
        controlType: ThresholdControlType.dropdown,
        dropdownOptions: ['Low/Sparse', 'Medium', 'High/Solid'],
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 2,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.impactContrails]: {
        name: 'Impact: Contrail',
        controlType: ThresholdControlType.dropdown,
        dropdownOptions: ['Low', 'Moderate', 'High', 'Extreme'],
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 3,
        alertsOnly: false,
        disabled: false
    },
    [AircraftThresholdType.enterDrawingObject]: {
        name: 'Enter drawing object',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.exitDrawingObject]: {
        name: 'Exit drawing object',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.flightPlanChanged]: {
        name: 'Flight plan changed',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    },
    [AircraftThresholdType.routeEteChanged]: {
        name: 'Route ETE changed',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    }
};

export const commonAirportThresholdDefinitions: Record<AirportThresholdType, ThresholdInfo> = {
    [AirportThresholdType.metarFlightCat]: {
        name: 'METAR flight category',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: ['VFR', 'MVFR', 'IFR', 'LIFR', 'BCAT1'],
        min: 0,
        max: 4,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.metarVisibility]: {
        name: 'METAR visibility',
        customUnits: [
            { displayName: 'SM', multiplier: 1 },
            { displayName: 'm', multiplier: 1609 }
        ],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: true,
        canRepeat: true,
        min: 0,
        max: 105000,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.metarCeiling]: {
        name: 'METAR ceiling',
        units: 'feet',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 33000,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.metarWindSpeed]: {
        name: 'METAR winds',
        units: 'knots',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 250,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.metarWindGusts]: {
        name: 'METAR wind gusts',
        units: 'knots',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 250,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.metarLastUpdate]: {
        name: 'METAR last update',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 720,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.tafFlightCat]: {
        name: 'TAF flight category',
        controlType: ThresholdControlType.dropdown,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        dropdownOptions: ['VFR', 'MVFR', 'IFR', 'LIFR', 'BCAT1'],
        min: 0,
        max: 4,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.tafVisibility]: {
        name: 'TAF visibility',
        customUnits: [
            { displayName: 'SM', multiplier: 1 },
            { displayName: 'm', multiplier: 1609 }
        ],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: true,
        canRepeat: true,
        min: 0,
        max: 105000,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.tafCeiling]: {
        name: 'TAF ceiling',
        units: 'feet',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 33000,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.tafWindSpeed]: {
        name: 'TAF winds',
        units: 'knots',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 250,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.tafLastUpdate]: {
        name: 'TAF last update',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: true,
        min: 0,
        max: 720,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.departDelay]: {
        name: 'Departure delay',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.arrivalDelay]: {
        name: 'Arrival delay',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.groundStop]: {
        name: 'Ground stop',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.groundDelay]: {
        name: 'Ground delay',
        units: 'minutes',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 360,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.faaClosure]: {
        name: 'FAA closure',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.rvr]: {
        name: 'RVR (real-time)',
        units: 'feet',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 6000,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.diversionsTo]: {
        name: 'Diversions to',
        units: 'diversions',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 100,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.diversionsFrom]: {
        name: 'Diversion cap avail',
        units: 'percentage',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 100,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.departureRate]: {
        name: 'Departure rate',
        units: 'departures/hour',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 200,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.arrivalRate]: {
        name: 'Arrival rate',
        units: 'arrivals/hour',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.down,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 200,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.deIcingActive]: {
        name: 'De-icing active',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.holdingInto]: {
        name: 'Flights Holding into',
        units: '# holding',
        customUnits: [],
        controlType: ThresholdControlType.input,
        worseningDirection: ThresholdWorseningDirection.up,
        decimal: false,
        canRepeat: false,
        min: 1,
        max: 255,
        alertsOnly: false,
        disabled: false
    },
    [AirportThresholdType.monitorStatusChanged]: {
        name: 'Monitor status change',
        controlType: ThresholdControlType.checkbox,
        worseningDirection: ThresholdWorseningDirection.none,
        decimal: false,
        canRepeat: false,
        min: 0,
        max: 0,
        alertsOnly: true,
        disabled: false
    }
};
